import type { UUID } from "utils";
import type { APPLICATION_ENV } from "../../../constants/environment";
import type { Environment } from "../models/CustomerCredentials";
import type { ProductFlagsType } from "../models/ProductFlags";

const BRAZIL_SETTLEMENT_ID = "brazil-settlement";
const COUNTRIES_CARD = "countries-card";
const COUNTRIES_CARD_PREVIEW_LIST = "countries-card-preview-list";
const COUNTRIES_CARD_EDIT_LIST = "countries-card-edit-list";
const PRODUCT_FLAG = "product-flag";

export const TEST_IDS = {
  addAddressButton: "customer-add-address-button",
  addButton: "add-button",
  addDynamicBeneficiary: "dynamic-beneficiary-add-button",
  createForm: "create-customer-form",
  addressBuildingNumber: "customer-address-building-number",
  addressCardLoading: "customer-address-card-loading",
  addressCity: "customer-address-city",
  addressCountry: "customer-address-country",
  addressCreate: "customer-address-create",
  addressEdit: "customer-address-edit",
  addressLine2: "customer-address-line2",
  addressPostalZipCode: "customer-address-postal-zip-code",
  addressState: "customer-address-state",
  addressStreet: "customer-address-street",
  bankAccountCheckbox: "bank-account-checkbox",
  bankAccountControl: "bank-account-control",
  brazilAccountsAutocomplete: `${BRAZIL_SETTLEMENT_ID}-accounts-autocomplete`,
  brazilClearButton: `${BRAZIL_SETTLEMENT_ID}-clear-button`,
  brazilSettlementCard: `${BRAZIL_SETTLEMENT_ID}-card`,
  brazilSettlementForm: `${BRAZIL_SETTLEMENT_ID}-form`,
  brazilSettlementHelperText: `${BRAZIL_SETTLEMENT_ID}-helper-text`,
  brazilSettlementValidation: `${BRAZIL_SETTLEMENT_ID}-validation`,
  breadcrumbAddress: "breadcrumb-address",
  breadcrumbConfiguration: "breadcrumb-configuration",
  breadcrumbCreate: "breadcrumb-create",
  breadcrumbDetails: "breadcrumb-details",
  breadcrumbDynamicBeneficiariesCreate:
    "breadcrumb-dynamic-beneficiaries-create",
  breadcrumbDynamicBeneficiariesList: "breadcrumb-dynamic-beneficiaries-list",
  breadcrumbEdit: "breadcrumb-edit",
  breadcrumbList: "breadcrumb-list",
  breadcrumbPaymentConfiguration: "breadcrumb-payment-configuration",
  chooseDeactivationDateButton: "choose-deactivation-date-button",
  circuitBreaker: "customer-circuit-breaker",
  clearGlobalFilterSuccess: "clear-global-filter-success",
  closeCredentialsDialog: "close-credentials-dialog-button",
  closeGenerateCredentials: "close-generate-credentials-button",
  companyRegistrationNumber: "customer-company-registration-number",
  configureButton: `${COUNTRIES_CARD_EDIT_LIST}-configure-button`,
  configureEnabledCountries: "configure-enabled-countries",
  countriesCard: COUNTRIES_CARD,
  countriesCardEditList: COUNTRIES_CARD_EDIT_LIST,
  countriesCardEditListLoading: `${COUNTRIES_CARD_EDIT_LIST}-loading`,
  countriesCardLoading: `${COUNTRIES_CARD}-loading`,
  countriesCardPreviewList: `${COUNTRIES_CARD_PREVIEW_LIST}`,
  countriesCardPreviewListLoading: `${COUNTRIES_CARD_PREVIEW_LIST}-loading`,
  country: "country-code",
  countryFormError: "country-form-error",
  countryFormInput: "country-form-input",
  countryFormInputCard: "country-form-input-card",
  countryFormLabel: "country-form-label",
  credentialsContentAlert: "generate-credentials-alert",
  credentialsDialog: "credentials-dialog",
  credentialsDialogContent: "credentials-dialog-content",
  credentialsDialogError: "credentials-dialog-error-alert",
  credentialsDialogPassword: "credentials-dialog-password",
  credentialsDialogTitle: "credentials-dialog-title",
  credentialsDialogUsername: "credentials-dialog-username",
  customerFiltersActions: "customer-filters-actions",
  customerFiltersContent: "customer-filters-content",
  customerFiltersModal: "customer-filters-modal",
  customerTypeFilter: "customer-type-filter",
  customerTypeTagSelect: "customer-type-tag-select",
  deactivationBanner: "customer-deactivation-banner",
  deactivationCustomerCard: "deactivation-customer-card",
  discr: "customer-discr",
  documentNumber: "customer-document-number",
  dynamicBeneficiariesUpdateFailure:
    "dynamic-beneficiaries-update-failure-toast",
  dynamicBeneficiariesUpdateSuccess:
    "dynamic-beneficiaries-update-success-toast",
  dynamicBeneficiaryBankCodeInput: "dynamic-beneficiary-bank-code-input",
  dynamicBeneficiaryCreateForm: "dynamic-beneficiary-create-form",
  dynamicBeneficiaryListActionBarButton:
    "dynamic-beneficiary-list-action-bar-button",
  dynamicBeneficiaryUsernamesFailure:
    "dynamic-beneficiary-usernames-failure-toast",
  editButton: "edit-button",
  editCustomerAddressLink: "edit-customer-address-link",
  editCustomerButton: "edit-customer-button",
  editCustomerDetailsLink: "edit-customer-details-link",
  formCancelButton: "form-cancel-button",
  formSubmitButton: "form-submit-button",
  globalFilterButton: "customer-global-filter-id",
  hierarchy: "customer-hierarchy",
  id: "customer-id",
  incorporationDate: "customer-incorporation-date",
  legalName: "customer-legal-name",
  merchantCategoryCode: "customer-merchant-category-code",
  name: "customer-name",
  parent: "customer-parent",
  payByLink: "pay-by-link",
  productFlagActiveCard: `${PRODUCT_FLAG}-active-card`,
  productFlagCardLoading: `${PRODUCT_FLAG}-card-loading`,
  productFlagConfigure: `${PRODUCT_FLAG}-configure-button`,
  productFlagConfigureModal: `${PRODUCT_FLAG}-configure-modal`,
  productFlagFindOutMore: `${PRODUCT_FLAG}-find-out-more-button`,
  productFlagInactiveCard: `${PRODUCT_FLAG}-inactive-card`,
  productFlagNameCell: `${PRODUCT_FLAG}-name-cell`,
  refund: "customer-refund",
  payouts: "customer-payouts",
  refundSwitchLabel: "customer-refund-switch-label",
  pageHeaderTitle: "page-header-title",
  payoutSwitchLabel: "customer-payouts-switch-label",
  removeDynamicBeneficiary: "dynamic-beneficiary-remove-button",
  settlementValidatorBankName: `${BRAZIL_SETTLEMENT_ID}-validator-bank-name`,
  status: "customer-status",
  statusLabel: "customer-status-label",
  statusModal: "customer-status-modal",
  statusToggle: "customer-status-toggle",
  stopDeactivationButton: "customer-stop-deactivation-button",
  stopDeactivationModal: "customer-stop-deactivation-modal",
  tabCashManagement: "cash-management-tab",
  tabCheckoutBranding: "checkout-branding-tab",
  tabCustomerDetails: "customer-details-tab",
  tabDynamicBeneficiaries: "dynamic-beneficiaries-tab",
  tabPaymentConfiguration: "payment-configuration-tab",
  terminationDatePicker: "customer-termination-datepicker",
  terminationForm: "customer-termination-form",
  terminationImmediatelyCheckbox: "customer-termination-immediately-checkbox",
  transformer: "customer-transformer",
  updateBrazilSettlementFailure: "update-brazil-settlement-failure-toast",
  updateBrazilSettlementSuccess: "update-brazil-settlement-success-toast",
  validatorCompanyDetails: `${BRAZIL_SETTLEMENT_ID}-validator-company-details`,
  validatorCustomerAddress: `${BRAZIL_SETTLEMENT_ID}-validator-customer-address`,
  verify: "customer-verify",
  getAddressFormInput: (field: string) => `${field}-input`,
  getBankAccountCardId: (id: UUID) => `bank-account-card-${id}`,
  getConfigureButtonId: (providerName: string) =>
    `${providerName}-configure-button`,
  getCredentialsListRow: (environment: Environment) =>
    `${environment}-credentials-list-row`,
  getCredentialsUserName: (environment: Environment) =>
    `${environment}-credentials-user-name`,
  getDynamicBeneficiaryBankCode: (index: number) =>
    `dynamic-beneficiary-bank-code-${index}`,
  getDynamicBeneficiaryRow: (id: UUID) => `dynamic-beneficiary-id-${id}`,
  getGenerateCredentialsButton: (environment: Environment) =>
    `${environment}-generate-credentials-button`,
  getRegenerateCredentialsButton: (environment: Environment) =>
    `${environment}-regenerate-credentials-button`,
  getPaymentPreferenceToggleId: (type: string) =>
    `payment-preference-${type}-toggle`,
  getProductFlagRow: (productFlag: string) => `${PRODUCT_FLAG}-${productFlag}`,
  getProductFlagTag: (environment: string) =>
    `${PRODUCT_FLAG}-${environment}-tag`,
  getProviderConfigError: (providerName: string) =>
    `${providerName}-config-error`,
  getProviderConfigInput: (providerName: string) =>
    `${providerName}-config-input`,
  getProviderConfigLabel: (providerName: string) =>
    `${providerName}-config-label`,
  getProviderRowId: (providerName: string) => `${providerName}-row-provider`,
  getTagId: (label: string) => `${label}-tag`,
  getToggleEnvironment: (
    productFlagId: ProductFlagsType,
    environment: APPLICATION_ENV
  ) => `${productFlagId}-${environment}-toggle`,
  getToggleEnvironmentWrapper: (
    productFlagId: ProductFlagsType,
    environment: APPLICATION_ENV
  ) => `${productFlagId}-${environment}-toggle-wrapper`,
  getToggleId: (providerName: string) => `${providerName}-toggle`,
};
